export const GRAND_PRIZE_DISTRIBUTION = [0.5, 0.3, 0.15, 0.05] as const;

export interface PrizePlaceAmounts {
  prize_first_place: number;
  prize_second_place: number;
  prize_third_place: number;
  prize_fourth_place: number;
  prize_fifth_place?: number;
  prize_sixth_place?: number;
  prize_seventh_place?: number;
  prize_eighth_place?: number;
  secondary_prize?: number;
  dynamic_prizes?: Record<string, number>;
}

export function distributeGrandPrize(grandPrize: number): Pick<
  PrizePlaceAmounts,
  'prize_first_place' | 'prize_second_place' | 'prize_third_place' | 'prize_fourth_place'
> {
  return {
    prize_first_place: grandPrize * GRAND_PRIZE_DISTRIBUTION[0],
    prize_second_place: grandPrize * GRAND_PRIZE_DISTRIBUTION[1],
    prize_third_place: grandPrize * GRAND_PRIZE_DISTRIBUTION[2],
    prize_fourth_place: grandPrize * GRAND_PRIZE_DISTRIBUTION[3],
  };
}

// SB-006: reparto del Gran Premio consciente del número de ganadores (1..4).
// Con 1 ganador el 100% va al primero; con 2: 60/40; con 3: 50/30/20; con 4+
// se usa el reparto estándar 50/30/15/5. Los lugares no usados quedan en 0.
const GRAND_PRIZE_DISTRIBUTION_BY_PLACES: Record<number, number[]> = {
  1: [1],
  2: [0.6, 0.4],
  3: [0.5, 0.3, 0.2],
  4: [0.5, 0.3, 0.15, 0.05],
};

export function distributeGrandPrizeByPlaces(
  grandPrize: number,
  numberOfPlaces: number
): Pick<
  PrizePlaceAmounts,
  'prize_first_place' | 'prize_second_place' | 'prize_third_place' | 'prize_fourth_place'
> {
  const topPlaces = Math.min(Math.max(1, Math.floor(numberOfPlaces) || 1), 4);
  const dist = GRAND_PRIZE_DISTRIBUTION_BY_PLACES[topPlaces] || [...GRAND_PRIZE_DISTRIBUTION];
  const amountAt = (idx: number) => (dist[idx] != null ? grandPrize * dist[idx] : 0);
  return {
    prize_first_place: amountAt(0),
    prize_second_place: amountAt(1),
    prize_third_place: amountAt(2),
    prize_fourth_place: amountAt(3),
  };
}

const round2 = (value: number): number => Math.round((Number(value) || 0) * 100) / 100;
const ceil2 = (value: number): number => Math.ceil((Number(value) || 0) * 100) / 100;

// =============================================================================
// PREMIO SECUNDARIO — Distribución por BANDAS
// -----------------------------------------------------------------------------
// Los lugares secundarios se reparten en 4 bandas según el monto por lugar
// ("premio por ciclo"). El número de lugares que cae en cada banda se decide por
// un PORCENTAJE de los lugares totales (no por el monto). Dentro de cada banda
// cada lugar recibe un monto acotado a [min, max] de la banda.
//
//   Banda    Rango por lugar    % de lugares
//   ------   ----------------   ------------
//   Alta     $1.00 – $2.00      5%
//   Media    $0.30 – $0.99      40%
//   Baja     $0.15 – $0.49      45%
//   Mínima   $0.01 – $0.14      10%
// =============================================================================

export type SecondaryBandKey = 'alta' | 'media' | 'baja' | 'minima';

export interface SecondaryBand {
  key: SecondaryBandKey;
  label: string;
  /** Monto mínimo por lugar dentro de la banda. */
  min: number;
  /** Monto máximo por lugar dentro de la banda. */
  max: number;
  /** Fracción (0..1) de los lugares secundarios que caen en esta banda. */
  placesShare: number;
}

// Orden descendente (de mayor a menor premio) para que se muestre primero la
// banda más alta. Los porcentajes deben sumar 1.
export const SECONDARY_PRIZE_BANDS: readonly SecondaryBand[] = [
  { key: 'alta', label: 'Alta', min: 1.0, max: 2.0, placesShare: 0.05 },
  { key: 'media', label: 'Media', min: 0.3, max: 0.99, placesShare: 0.4 },
  { key: 'baja', label: 'Baja', min: 0.15, max: 0.49, placesShare: 0.45 },
  { key: 'minima', label: 'Mínima', min: 0.01, max: 0.14, placesShare: 0.1 },
] as const;

// Cota global por lugar (la más baja y la más alta entre todas las bandas).
// Se conservan estos nombres por compatibilidad con código existente.
export const SECONDARY_PRIZE_MIN = Math.min(...SECONDARY_PRIZE_BANDS.map(b => b.min));
export const SECONDARY_PRIZE_MAX = Math.max(...SECONDARY_PRIZE_BANDS.map(b => b.max));

// Reparte N lugares entre las bandas según `placesShare`, garantizando que la
// suma de los conteos sea exactamente N (método del mayor residuo).
export function allocateSecondaryBandCounts(numberOfPlaces: number): number[] {
  const n = Math.max(0, Math.floor(numberOfPlaces) || 0);
  if (n === 0) return SECONDARY_PRIZE_BANDS.map(() => 0);

  const raw = SECONDARY_PRIZE_BANDS.map(b => n * b.placesShare);
  const counts = raw.map(Math.floor);
  let remaining = n - counts.reduce((sum, c) => sum + c, 0);

  const byRemainder = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; remaining > 0; k = (k + 1) % byRemainder.length) {
    counts[byRemainder[k].index] += 1;
    remaining -= 1;
  }

  return counts;
}

// Devuelve la banda asignada a cada lugar (longitud N), ordenado de la banda
// más alta a la más baja, para que el lugar #1 sea el de mayor premio.
export function getSecondaryBandLayout(numberOfPlaces: number): SecondaryBand[] {
  const counts = allocateSecondaryBandCounts(numberOfPlaces);
  const layout: SecondaryBand[] = [];
  counts.forEach((count, bandIndex) => {
    for (let i = 0; i < count; i += 1) layout.push(SECONDARY_PRIZE_BANDS[bandIndex]);
  });
  return layout;
}

// Banda correspondiente a un lugar concreto según el conteo total de lugares.
export function getSecondaryBandForPlace(
  placeIndex: number,
  numberOfPlaces: number
): SecondaryBand | null {
  const layout = getSecondaryBandLayout(numberOfPlaces);
  return layout[placeIndex] ?? null;
}

// Rango total repartible para N lugares: [Σ band.min, Σ band.max].
// Como cada lugar admite cualquier valor en centavos dentro de su banda, la
// suma admite cualquier total (en centavos) dentro de este rango contiguo.
export function getSecondaryFeasibleRange(numberOfPlaces: number): {
  min: number;
  max: number;
} {
  const layout = getSecondaryBandLayout(numberOfPlaces);
  const min = round2(layout.reduce((sum, b) => sum + b.min, 0));
  const max = round2(layout.reduce((sum, b) => sum + b.max, 0));
  return { min, max };
}

// Acota un monto al rango de la banda a la que pertenece el lugar.
export function clampSecondaryPrizeForPlace(
  value: number,
  placeIndex: number,
  numberOfPlaces: number
): number {
  const band = getSecondaryBandForPlace(placeIndex, numberOfPlaces);
  const v = Number(value) || 0;
  if (v <= 0) return 0;
  if (!band) return round2(Math.min(SECONDARY_PRIZE_MAX, Math.max(SECONDARY_PRIZE_MIN, v)));
  return round2(Math.min(band.max, Math.max(band.min, v)));
}

// Acota al rango global (compatibilidad). Prefiere clampSecondaryPrizeForPlace.
export const clampSecondaryPrize = (value: number): number => {
  const v = Number(value) || 0;
  if (v <= 0) return 0;
  return round2(Math.min(SECONDARY_PRIZE_MAX, Math.max(SECONDARY_PRIZE_MIN, v)));
};

// Ajusta montos en centavos para que la suma coincida con `targetTotal`,
// priorizando sumar a lugares con más peso (más arriba) y restar a los de abajo.
function reconcileSecondaryAmounts(
  amounts: number[],
  layout: SecondaryBand[],
  targetTotal: number
): void {
  const toCents = (v: number) => Math.round(round2(v) * 100);
  const fromCents = (c: number) => round2(c / 100);
  const n = amounts.length;
  if (n === 0) return;

  const weights = Array.from({ length: n }, (_, i) => n - i);
  const orderAdd = [...weights.keys()].sort((a, b) => weights[b] - weights[a]);
  const orderSub = [...weights.keys()].sort((a, b) => weights[a] - weights[b]);

  const cents = amounts.map(toCents);
  const mins = layout.map(b => toCents(b.min));
  const maxs = layout.map(b => toCents(b.max));
  let diff = toCents(targetTotal) - cents.reduce((sum, c) => sum + c, 0);

  for (let guard = 0; diff !== 0 && guard < 200_000; guard += 1) {
    let changed = false;
    if (diff > 0) {
      for (const i of orderAdd) {
        if (diff === 0) break;
        if (cents[i] < maxs[i]) {
          cents[i] += 1;
          diff -= 1;
          changed = true;
        }
      }
    } else {
      for (const i of orderSub) {
        if (diff === 0) break;
        if (cents[i] > mins[i]) {
          cents[i] -= 1;
          diff += 1;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  for (let i = 0; i < n; i += 1) amounts[i] = fromCents(cents[i]);
}

// Construye la curva descendente "ideal" para N lugares: dentro de cada banda
// baja en escalera lineal del máximo al mínimo (p. ej. Alta de 3 lugares →
// $2.00, $1.50, $1.00). Como las bandas ya vienen ordenadas de mayor a menor,
// el resultado es SIEMPRE estrictamente no creciente (#1 ≥ #2 ≥ … ≥ #N).
export function buildSecondaryIdealDescending(numberOfPlaces: number): number[] {
  const layout = getSecondaryBandLayout(numberOfPlaces);
  const n = layout.length;
  if (n === 0) return [];

  // Agrupar lugares consecutivos por banda.
  const groups: { band: SecondaryBand; start: number; count: number }[] = [];
  for (let i = 0; i < n; ) {
    const band = layout[i];
    let count = 1;
    while (i + count < n && layout[i + count].key === band.key) count += 1;
    groups.push({ band, start: i, count });
    i += count;
  }

  const ideal = new Array<number>(n).fill(0);
  groups.forEach(group => {
    const { band, start, count } = group;
    for (let k = 0; k < count; k += 1) {
      const t = count === 1 ? 0 : k / (count - 1);
      ideal[start + k] = round2(band.max - t * (band.max - band.min));
    }
  });

  // Las bandas se solapan en valor (p. ej. Media baja a $0.30 y Baja llega a
  // $0.49), así que forzamos descenso GLOBAL: cada lugar no puede superar al
  // anterior. El recorte siempre cae dentro del rango de su banda porque el
  // mínimo de la banda anterior ≥ el mínimo de la banda actual.
  for (let i = 1; i < n; i += 1) {
    if (ideal[i] > ideal[i - 1]) ideal[i] = round2(Math.max(layout[i].min, ideal[i - 1]));
  }
  return ideal;
}

// Fuerza que la secuencia sea no creciente respetando el mínimo de cada banda.
function enforceNonIncreasing(amounts: number[], layout: SecondaryBand[]): void {
  for (let i = 1; i < amounts.length; i += 1) {
    if (amounts[i] > amounts[i - 1]) {
      amounts[i] = round2(Math.max(layout[i].min, amounts[i - 1]));
    }
  }
}

// Rango "repartible con descenso" para N lugares.
//  - min = suma de los mínimos de cada banda (todos al piso).
//  - max = suma de la curva descendente ideal (máximo manteniendo el descenso).
// Es deliberadamente menor que Σband.max: ese tope solo se alcanza poniendo
// TODOS al máximo (sin descenso), que es justo lo que NO queremos.
export function getSecondaryDistributableRange(numberOfPlaces: number): {
  min: number;
  max: number;
} {
  const layout = getSecondaryBandLayout(numberOfPlaces);
  const min = round2(layout.reduce((sum, b) => sum + b.min, 0));
  const max = round2(buildSecondaryIdealDescending(numberOfPlaces).reduce((s, v) => s + v, 0));
  return { min, max };
}

// Reparte `total` entre N lugares secundarios SIEMPRE de forma descendente
// (#1 gana más que #2, etc.). Parte de la curva descendente ideal y la escala
// al total entre [Σmin, ΣidealDescendente]:
//  - total ≥ max  → curva ideal completa (máximo descenso posible).
//  - total ≤ min  → todos en el mínimo de su banda.
//  - intermedio   → interpola entre el piso y la curva ideal (suma = total exacto).
// Nunca pone todos al tope: el orden descendente se preserva en todos los casos.
export function distributeSecondaryPrizeByBands(
  total: number,
  numberOfPlaces: number
): number[] {
  const layout = getSecondaryBandLayout(numberOfPlaces);
  const n = layout.length;
  if (n === 0) return [];

  const safeTotal = round2(Number(total) || 0);
  if (safeTotal <= 0) return layout.map(() => 0);

  const ideal = buildSecondaryIdealDescending(numberOfPlaces);
  const mins = layout.map(b => b.min);
  const minSum = round2(mins.reduce((s, v) => s + v, 0));
  const idealSum = round2(ideal.reduce((s, v) => s + v, 0));

  // Total en/por encima del máximo con descenso → curva ideal completa.
  if (safeTotal >= idealSum - 0.001) {
    return ideal.map(round2);
  }
  // Total en/por debajo del mínimo → todos al piso de su banda.
  if (safeTotal <= minSum + 0.001) {
    return mins.map(round2);
  }

  // Interpolación lineal entre el piso (mins) y la curva ideal, manteniendo el
  // descenso y haciendo que la suma sea exactamente el total.
  const span = idealSum - minSum || 1;
  const f = (safeTotal - minSum) / span;
  const amounts = ideal.map((idealValue, i) =>
    round2(mins[i] + f * (idealValue - mins[i]))
  );

  enforceNonIncreasing(amounts, layout);
  reconcileSecondaryAmounts(amounts, layout, safeTotal);
  enforceNonIncreasing(amounts, layout);
  return amounts.map(round2);
}

export type SecondaryTotalReason = 'below_min' | 'above_max' | null;

export interface SecondaryTotalValidation {
  /** El total se puede repartir exactamente con N lugares. */
  feasible: boolean;
  /** Mínimo repartible con N lugares. */
  min: number;
  /** Máximo repartible con N lugares. */
  max: number;
  /** Total válido sugerido (hacia arriba) o null si ya es válido. */
  suggested: number | null;
  reason: SecondaryTotalReason;
}

// Valida si `total` puede repartirse exactamente entre N lugares respetando las
// bandas. Si el total es menor al mínimo, sugiere el siguiente total válido HACIA
// ARRIBA (p. ej. 133 -> 134): el siguiente dólar entero factible o, si no cabe un
// dólar entero, el propio mínimo. Si excede el máximo, sugiere el máximo y pide
// reducir el monto o agregar lugares.
export function validateSecondaryTotal(
  total: number,
  numberOfPlaces: number
): SecondaryTotalValidation {
  const { min, max } = getSecondaryDistributableRange(numberOfPlaces);
  const t = round2(Number(total) || 0);
  const places = Math.max(0, Math.floor(numberOfPlaces) || 0);

  if (places === 0 || t <= 0) {
    return { feasible: true, min, max, suggested: null, reason: null };
  }

  if (t < min) {
    const nextWhole = Math.ceil(min);
    const suggested = nextWhole <= max ? nextWhole : ceil2(min);
    return { feasible: false, min, max, suggested, reason: 'below_min' };
  }

  if (t > max) {
    return { feasible: false, min, max, suggested: round2(max), reason: 'above_max' };
  }

  return { feasible: true, min, max, suggested: null, reason: null };
}

export function sumGrandPrizePlaces(prizes: PrizePlaceAmounts): number {
  return (
    (Number(prizes.prize_first_place) || 0) +
    (Number(prizes.prize_second_place) || 0) +
    (Number(prizes.prize_third_place) || 0) +
    (Number(prizes.prize_fourth_place) || 0)
  );
}

export function getDynamicPrizesTotal(dynamicPrizes: unknown): number {
  if (!dynamicPrizes || typeof dynamicPrizes !== 'object') return 0;
  return Object.values(dynamicPrizes as Record<string, unknown>).reduce<number>(
    (sum, value) => sum + (Number(value) || 0),
    0
  );
}

export function sumSecondaryPrizePool(prizes: PrizePlaceAmounts): number {
  const dynamicTotal = getDynamicPrizesTotal(prizes.dynamic_prizes);
  if (dynamicTotal > 0) return dynamicTotal;

  const legacyTotal =
    (Number(prizes.prize_fifth_place) || 0) +
    (Number(prizes.prize_sixth_place) || 0) +
    (Number(prizes.prize_seventh_place) || 0) +
    (Number(prizes.prize_eighth_place) || 0);

  if (legacyTotal > 0) return legacyTotal;
  return Number(prizes.secondary_prize) || 0;
}

export function calculateTotalPrizePool(prizes: PrizePlaceAmounts): number {
  return sumGrandPrizePlaces(prizes) + sumSecondaryPrizePool(prizes);
}

export function getGrandPrizeSharePercent(amount: number, grandPrizeTotal: number): number {
  if (grandPrizeTotal <= 0) return 0;
  return (amount / grandPrizeTotal) * 100;
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}
